import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, RefreshCw, Eye, Pencil, Trash2, FileText, Send, X, Copy,
  ChevronLeft, ChevronRight, Printer, Download, Settings, Loader2, Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cotizacionesApi, receptoresApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import InvoiceTemplate from '../../components/InvoiceTemplate';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import MonthRangePicker from '../../components/MonthRangePicker';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50",
    success: "border border-green-400 text-green-600 hover:bg-green-50",
    amber: "border border-amber-400 text-amber-600 hover:bg-amber-50",
    danger: "border border-red-300 text-red-600 hover:bg-red-50",
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

export default function Cotizaciones() {
  const navigate = useNavigate();
  const { currentEmisor } = useAuthStore();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [emailConfig, setEmailConfig] = useState({ azureTenantId: '', azureClientId: '', azureClientSecret: '', fromEmail: '', fromName: '', replyToEmail: '' });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [emailConfigSaving, setEmailConfigSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('Cotizaciones', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });
  const [filtroEstado, setFiltroEstado] = useState('');
  const PAGE_SIZE = 30;
  const printRef = useRef();
  const { searchRef, buttonsRef } = useToolbarResponsive([filtroFecha, selectedIds.size]);

  const fetchCotizaciones = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: search, page, limit: PAGE_SIZE };
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroFecha?.desde) params.dateFrom = filtroFecha.desde;
      if (filtroFecha?.hasta) params.dateTo = filtroFecha.hasta;

      const response = await cotizacionesApi.list(params);
      setCotizaciones(response.data?.data || []);
      setTotal(response.data?.pagination?.total || response.data?.data?.length || 0);
      setTotalPages(response.data?.pagination?.pages || 1);
    } catch (error) {
      console.error('Error loading cotizaciones:', error);
    } finally {
      setLoading(false);
    }
  }, [search, page, filtroFecha, filtroEstado]);

  useEffect(() => { fetchCotizaciones(); }, [fetchCotizaciones]);
  // La selección solo guarda cotizaciones que se ven en la lista: al cambiar de mes o buscar, las de antes
  // seguían marcadas sin verse, apagaban Copiar/Generar OC y Eliminar las borraba también.
  useEffect(() => {
    setSelectedIds(prev => {
      const visibles = new Set(cotizaciones.map(c => c.id));
      const quedan = [...prev].filter(id => visibles.has(id));
      return quedan.length === prev.size ? prev : new Set(quedan);
    });
  }, [cotizaciones]);
  useEffect(() => { setPage(1); }, [currentEmisor]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === cotizaciones.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cotizaciones.map(c => c.id)));
    }
  };

  const selectedCots = cotizaciones.filter(c => selectedIds.has(c.id));

  const handleDeleteSelected = async () => {
    let deleted = 0;
    for (const id of selectedIds) {
      try {
        await cotizacionesApi.delete(id);
        deleted++;
      } catch (error) { /* continue */ }
    }
    setShowDeleteModal(false);
    setSelectedIds(new Set());
    toast.success(`${deleted} cotización(es) eliminada(s)`);
    fetchCotizaciones();
  };

  const handleConvertToInvoice = async () => {
    const cot = selectedCots[0];
    try {
      const response = await cotizacionesApi.convert(cot.id);
      if (response.data?.success) {
        toast.success('Cotización convertida a factura exitosamente');
        setSelectedIds(new Set());
        fetchCotizaciones();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al convertir cotización');
    }
  };

  const handleVer = async (cot) => {
    setPreviewLoading(true);
    setShowPreview(cot);
    try {
      // Get full cotizacion with lineas
      const cotRes = await cotizacionesApi.getById(cot.id);
      const cotFull = cotRes.data?.data || cotRes.data;

      // Get receptor full data if exists
      let receptorFull = null;
      if (cotFull.receptorId) {
        try {
          const recRes = await receptoresApi.getById(cotFull.receptorId);
          receptorFull = recRes.data?.data || recRes.data;
        } catch (e) { /* ignore */ }
      }

      // Map to InvoiceTemplate format
      const documento = {
        consecutivo: cotFull.numero || `COT-${String(cotFull.id).padStart(4, '0')}`,
        codigoMoneda: cotFull.codigoMoneda || 'USD',
        fechaEmision: cotFull.fecha,
        diasCredito: cotFull.fechaVencimiento
          ? Math.round((new Date(cotFull.fechaVencimiento) - new Date(cotFull.fecha)) / (1000 * 60 * 60 * 24))
          : 30,
        notas: cotFull.notas,
      };

      const lineas = (cotFull.lineas || []).map(l => {
        // Find matching exoneration for this line
        let impuestoExoneracion = null;
        if (receptorFull?.exoneraciones?.length > 0 && l.impuestoMonto === 0) {
          const cabys = l.cabys || l.codigoCabys || '';
          for (const exo of receptorFull.exoneraciones) {
            const exoCabys = exo.cabys;
            if (!exoCabys || !Array.isArray(exoCabys) || exoCabys.length === 0 ||
                exoCabys.some(c => cabys === c)) {
              const sub = (l.cantidad || 1) * (l.precioUnitario || 0);
              impuestoExoneracion = { porcentaje: exo.porcentaje, monto: sub * exo.porcentaje / 100, numeroDocumento: exo.numeroDocumento, tipoDocumentoDesc: exo.tipoDocumentoDesc || 'Exoneración' };
              break;
            }
          }
        } else if (receptorFull?.exoneracionActiva && l.impuestoMonto === 0 && receptorFull.exoneracionPorcentaje) {
          const sub = (l.cantidad || 1) * (l.precioUnitario || 0);
          impuestoExoneracion = { porcentaje: receptorFull.exoneracionPorcentaje, monto: sub * receptorFull.exoneracionPorcentaje / 100, numeroDocumento: receptorFull.exoneracionNumero, tipoDocumentoDesc: 'Exoneración' };
        }

        return {
          codigo: l.cabys || l.codigoCabys,
          sku: l.codigo,
          detalle: l.detalle,
          cantidad: l.cantidad,
          precioUnitario: l.precioUnitario,
          descuentoMonto: l.descuentoMonto || l.descuento_monto || 0,
          impuestoTarifa: l.impuestoTarifa,
          impuestoMonto: l.impuestoMonto,
          // Impuestos adicionales (911, Cruz Roja, …) para que la plantilla los desglose
          impuestosAdicionales: l.impuestosAdicionales || l.impuestos_adicionales || [],
          montoTotalLinea: l.montoTotalLinea,
          impuestoExoneracion,
        };
      });

      const totales = {
        totalGravado: cotFull.subtotal,
        totalExento: 0,
        totalDescuentos: cotFull.totalDescuentos,
        totalImpuesto: cotFull.totalImpuesto,
        totalVenta: cotFull.subtotal - (cotFull.totalDescuentos || 0),
        totalComprobante: cotFull.total,
        totalOtrosCargos: 0,
      };

      // Override emisor terms with cotizacion terms
      const emisorOverride = currentEmisor ? { ...currentEmisor, terminos_condiciones: cotFull.terminos || '' } : null;

      setPreviewData({ documento, lineas, totales, receptor: receptorFull, emisor: emisorOverride, tipoCambio: cotFull.tipoCambio || 1 });
    } catch (error) {
      toast.error('Error al cargar cotización');
      setShowPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Cotización</title>
      <style>
        body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
      </head><body>${content.innerHTML}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  const handleDownloadPdf = async (cot) => {
    try {
      const response = await cotizacionesApi.downloadPdf(cot.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const cotNum = cot.numero || `COT-${String(cot.id).padStart(4, '0')}`;
      const clienteNombre = (cot.clienteNombre || cot.receptorNombre || cot.receptor_nombre || 'Cliente').replace(/[/\\?%*:|"<>]/g, '');
      a.download = `${cotNum} - ${clienteNombre}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al descargar PDF');
    }
  };

  const formatCurrency = (amount, moneda = 'CRC') => {
    if (!amount) return '-';
    const sym = moneda === 'USD' ? '$' : '₡';
    return `${sym}${parseFloat(amount).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-CR');
  };

  return (
    <div className="space-y-4">
      {/* Toolbar — buscador absolute izq, botones centrados al toolbar (responsive con hook) */}
      <div className="bg-white rounded-lg border p-3 relative">
        <div ref={searchRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por cliente, consecutivo..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm"
            />
          </div>
          <MonthRangePicker
            value={filtroFecha}
            onChange={(v) => { setFiltroFecha(v); setPage(1); }}
          />
        </div>
        <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
          <ToolbarButton
            icon={Plus}
            label="Nueva Cotización"
            onClick={() => navigate('/facturacion/cotizaciones/nueva')}
            variant="amber"
          />
          <ToolbarButton
            icon={RefreshCw}
            label="Actualizar"
            onClick={fetchCotizaciones}
            disabled={loading}
          />
          <ToolbarButton
            icon={Send}
            label="Convertir a FE"
            onClick={handleConvertToInvoice}
            disabled={selectedIds.size !== 1 || ['convertida', 'facturada', 'rechazada'].includes(selectedCots[0]?.estado)}
            variant="success"
          />
          <ToolbarButton
            icon={FileText}
            label="Generar OC"
            onClick={() => navigate(`/compras/ordenes-compra/desde-cotizacion/${[...selectedIds][0]}`)}
            disabled={selectedIds.size !== 1 || ['rechazada', 'vencida'].includes(selectedCots[0]?.estado)}
            variant="primary"
          />
          <ToolbarButton
            icon={Copy}
            label="Copiar"
            onClick={async () => {
              const id = [...selectedIds][0];
              try {
                toast.loading('Duplicando...', { id: 'dup' });
                await cotizacionesApi.duplicate(id);
                toast.success('Cotización duplicada', { id: 'dup' });
                fetchCotizaciones();
                setSelectedIds(new Set());
              } catch (e) { toast.error(e.response?.data?.error || 'Error al duplicar', { id: 'dup' }); }
            }}
            disabled={selectedIds.size !== 1}
          />
          <ToolbarButton
            icon={Trash2}
            label="Eliminar"
            onClick={() => setShowDeleteModal(true)}
            disabled={selectedIds.size === 0}
            variant="danger"
          />
          <ToolbarButton
            icon={Settings}
            label="Config Email"
            onClick={async () => {
              setShowEmailConfig(true);
              setEmailConfigLoading(true);
              try {
                const res = await cotizacionesApi.getEmailConfig();
                if (res.data?.data) {
                  const d = res.data.data;
                  setEmailConfig({
                    azureTenantId: d.azure_tenant_id || d.azureTenantId || '',
                    azureClientId: d.azure_client_id || d.azureClientId || '',
                    azureClientSecret: '',
                    fromEmail: d.from_email || d.fromEmail || '',
                    fromName: d.from_name || d.fromName || '',
                    replyToEmail: d.reply_to_email || d.replyToEmail || ''
                  });
                }
              } catch (e) { /* no config yet */ }
              setEmailConfigLoading(false);
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            Cargando cotizaciones...
          </div>
        ) : cotizaciones.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay cotizaciones</h3>
            <p className="text-gray-500 mb-4">Cree su primera cotización</p>
            <button
              onClick={() => navigate('/facturacion/cotizaciones/nueva')}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-500 text-amber-600 rounded-lg hover:bg-amber-50"
            >
              <Plus className="h-4 w-4" />
              Nueva Cotización
            </button>
          </div>
        ) : (
          <div className="overflow-auto max-h-[calc(var(--alto-contenido)-60px)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-10 px-2 py-3">
                    <input type="checkbox"
                      checked={cotizaciones.length > 0 && selectedIds.size === cotizaciones.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded text-amber-600 focus:ring-amber-500 cursor-pointer" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">N°</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Fecha</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Cliente</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Subtotal</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Impuesto</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Total</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Moneda</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Estado</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700 w-40">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cotizaciones.map((cot) => (
                  <tr key={cot.id} className={`cursor-pointer hover:bg-gray-50 ${selectedIds.has(cot.id) ? 'bg-amber-50' : ''}`}
                    onClick={() => toggleSelect(cot.id)}>
                    <td className="px-2 py-3 text-center">
                      <input type="checkbox" checked={selectedIds.has(cot.id)} readOnly
                        className="h-4 w-4 rounded text-amber-600 focus:ring-amber-500 cursor-pointer" />
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                        {cot.numero || `COT-${String(cot.id).padStart(4, '0')}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(cot.fecha || cot.fechaEmision || cot.fecha_emision)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{cot.clienteNombre || cot.receptorNombre || cot.receptor_nombre || 'Sin cliente'}</div>
                      {(cot.receptorIdentificacion || cot.receptor_identificacion) && (
                        <div className="text-xs text-gray-500">{cot.receptorIdentificacion || cot.receptor_identificacion}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">{formatCurrency(cot.subtotal || cot.totalVentaNeta || cot.total_venta_neta, cot.codigoMoneda || cot.codigo_moneda)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(cot.totalImpuesto || cot.total_impuesto, cot.codigoMoneda || cot.codigo_moneda)}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatCurrency(cot.total || cot.totalComprobante || cot.total_comprobante, cot.codigoMoneda || cot.codigo_moneda)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{cot.codigoMoneda || cot.codigo_moneda || 'CRC'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const estado = cot.estado;
                        const colors = {
                          borrador: 'bg-gray-100 text-gray-700',
                          enviada: 'bg-blue-100 text-blue-700',
                          reenviada: 'bg-indigo-100 text-indigo-700',
                          aceptada: 'bg-green-100 text-green-700',
                          rechazada: 'bg-red-100 text-red-700',
                          vencida: 'bg-amber-100 text-amber-700',
                          convertida: 'bg-purple-100 text-purple-700',
                          facturada: 'bg-emerald-100 text-emerald-700'
                        };
                        const labels = {
                          borrador: 'Borrador',
                          enviada: 'Enviada al Cliente',
                          reenviada: 'Reenviada al Cliente',
                          aceptada: 'Aceptada',
                          rechazada: 'Rechazada',
                          vencida: 'Vencida',
                          convertida: 'Convertida a FE',
                          facturada: 'Facturada'
                        };
                        return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[estado] || 'bg-gray-100 text-gray-700'}`}>{labels[estado] || estado}</span>;
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleVer(cot); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-50 transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Ver
                        </button>
                        {!['convertida', 'facturada'].includes(cot.estado) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/facturacion/cotizaciones/${cot.id}/editar`); }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-50 transition-colors"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Editar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contador — esquina inferior izquierda, abajo de la tabla */}
      <div className="text-xs text-gray-500 mt-1 px-1">
        {total} {total === 1 ? 'cotización' : 'cotizaciones'}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg border px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Mostrando {((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, total)} de {total}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-700">Página {page} de {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Vista Previa - Cotización {showPreview.numero || `COT-${String(showPreview.id).padStart(4, '0')}`}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  disabled={previewLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir
                </button>
                <button
                  onClick={() => handleDownloadPdf(showPreview)}
                  disabled={previewLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-600 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Guardar PDF
                </button>
                <button onClick={() => { setShowPreview(null); setPreviewData(null); }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {previewLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
                  <span className="ml-3 text-gray-500">Cargando cotización...</span>
                </div>
              ) : previewData ? (
                <div className="bg-white shadow-sm mx-auto" style={{ maxWidth: '210mm' }}>
                  <div ref={printRef}>
                    <InvoiceTemplate
                      documento={previewData.documento}
                      emisor={previewData.emisor}
                      receptor={previewData.receptor}
                      lineas={previewData.lineas}
                      totales={previewData.totales}
                      esCotizacion={true}
                      tipoCambio={previewData.tipoCambio}
                      fullWidth={true}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Email Config Modal */}
      {showEmailConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="h-5 w-5 text-gray-500" />
                Configuración de Email - Cotizaciones
              </h2>
              <button onClick={() => setShowEmailConfig(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {emailConfigLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
                  <span className="ml-2 text-gray-500">Cargando...</span>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    Configure el buzón de Office 365 desde el cual se enviarán las cotizaciones. Se utiliza la misma App de Azure configurada para facturación.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email de envío *</label>
                    <input type="email" value={emailConfig.fromEmail}
                      onChange={e => setEmailConfig(p => ({ ...p, fromEmail: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="milama@empresa.com" />
                    <p className="text-xs text-gray-400 mt-1">Buzón de Office 365 autorizado en la App de Azure para enviar correos.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre remitente</label>
                    <input type="text" value={emailConfig.fromName}
                      onChange={e => setEmailConfig(p => ({ ...p, fromName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ventas - Mi Empresa" />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
              <button onClick={() => setShowEmailConfig(false)}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                Cancelar
              </button>
              <button
                disabled={emailConfigSaving || emailConfigLoading}
                onClick={async () => {
                  if (!emailConfig.fromEmail) {
                    toast.error('Ingrese el email de envío');
                    return;
                  }
                  setEmailConfigSaving(true);
                  try {
                    const payload = { ...emailConfig };
                    if (!payload.azureClientSecret) delete payload.azureClientSecret;
                    await cotizacionesApi.saveEmailConfig(payload);
                    toast.success('Configuración de email guardada');
                    setShowEmailConfig(false);
                  } catch (e) {
                    toast.error(e.response?.data?.error || 'Error al guardar');
                  }
                  setEmailConfigSaving(false);
                }}
                className="px-4 py-2 border-2 border-amber-500 text-amber-600 rounded-lg hover:bg-amber-50 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                {emailConfigSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Eliminar {selectedIds.size > 1 ? 'Cotizaciones' : 'Cotización'}</h2>
            <p className="text-gray-600 mb-4">
              ¿Está seguro que desea eliminar {selectedIds.size > 1
                ? `${selectedIds.size} cotizaciones seleccionadas`
                : <>la cotización <strong>{selectedCots[0]?.numero || `COT-${String(selectedCots[0]?.id).padStart(4, '0')}`}</strong></>
              }?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleDeleteSelected}
                className="px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                Eliminar{selectedIds.size > 1 ? ` (${selectedIds.size})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
